/**
 * These are to support legacy exports (see notes below).
 */
import { BaseService as baseService } from "./services/baseService";
import RouterClient from "./clients/routerClient";
import StorageClient from "./clients/storageClient";
import LauncherClient from "./clients/launcherClient";
import DistributedStoreClient from "./clients/distributedStoreClient";
import LinkerClient from "./deprecated/linkerClient";
import WindowClient from "./clients/windowClient";
import WorkspaceClient from "./clients/workspaceClient";
import DialogManager from "./clients/dialogManagerClient";
import AuthenticationClient from "./clients/authenticationClient";
import ConfigClient from "./clients/configClient";
import DragAndDropClient from "./deprecated/dragAndDropClient";
import Logger from "./clients/logger";
import { _BaseClient } from "./deprecated/baseClient";
import SearchClient from "./clients/searchClient";
import HotkeyClient from "./clients/hotkeyClient";
import NotificationClient from "./clients/notificationClient";
import UserNotification from "./common/toast";
import * as Util from "./common/util";
import DependencyManager from "./common/dependencyManager";
import baseStorage from "./services/storage/adapters/base";
import { System } from "./common/system";

/**
 * Imports
 */
import { FEAGlobals } from "./common/Globals";
import { FSBLDesktop } from "./clients/Startup/FSBLDesktop";
import { FEA } from "./FEA";
import { FinsembleWindow } from "./common/FinsembleWindow";
import { DesktopAgent } from "./typedefs/FDC3/index";
export * as services from "./services";
export * as clients from "./clients";
export * as types from "./types";
/**
 * @internal Don't display FEA in Typedoc output
 */
export * as FEA from "./FEA";
export * as common from "./common";
export * as deprecated from "./deprecated";
export * as constants from "./common/constants";

export * from "./clients";
export * from "./common";
export * from "./types";

// This line is necessary to force Typescript to build an index.d.ts file for /typedefs.
// Without this line, typescript will produce TS2688 errors in the seed
import * as Dummy from "./typedefs";

/**
 * Declare global variables that are injected either by preloads or by the FSBL library itself.
 */
declare global {
	/**
	 * FSBL is declared as "let" because we sometimes set FSBL.debug to true.
	 * This is type FSBLDesktop though in freestanding apps and services this isn't accurate.
	 * Use of ambient types should be avoided in those use cases. FSBL is added through
	 * the finsemble-desktop-api-preload (FSBL.js) that is preloaded by Launcher.
	 */
	let FSBL: typeof FEAGlobals.FSBL;

	/**
	 * The fin object is injected by FEA. Sometimes it's referred to as e2o.
	 */
	const fin: typeof FEAGlobals.fin;

	/**
	 * finsembleWindow is created on the fly by the FSBL client API initialization code.
	 */
	const finsembleWindow: typeof FEAGlobals.finsembleWindow;

	/**
	 * fdc3 is created on the fly by FSBLDesktop.
	 */
	const fdc3: typeof FEAGlobals.fdc3;
}

/**
 * Wait, didn't we just declare these? Yes, as globals:
 *
 * Example
 *  ```
 * FSBL.Clients.RouterClient...
 * ```
 *
 * but they don't exist on the `window` object. These following definitions are necessary to make this work:
 *
 * Example
 * ```
 * window.FSBL.Clients.RouterClient...
 * ```
 */
declare global {
	interface Window {
		/**
		 * FSBL is declared as "let" because we sometimes set FSBL.debug to true.
		 * This is type FSBLDesktop though in freestanding apps and services this isn't accurate.
		 * Use of ambient types should be avoided in those use cases. FSBL is added through
		 * the finsemble-desktop-api-preload (FSBL.js) that is preloaded by Launcher.
		 */
		FSBL: FSBLDesktop;

		/**
		 * The fin object is injected by FEA. Sometimes it's referred to as e2o.
		 */
		fin: FEA;

		/**
		 * finsembleWindow is created on the fly by the FSBL client API initialization code.
		 */
		finsembleWindow: FinsembleWindow;

		/**
		 * fdc3 is created on the fly by FSBLDesktop.
		 */
		fdc3: DesktopAgent;
	}
}

/**
 * Legacy ambient types
 *
 * These are types that are used in the client APIs and which previously
 * exported as root level ambient types. Customers that wrote typescript
 * code may have been using these types.
 */

// LauncherClient
export {
	WindowIdentifier,
	WindowDescriptor,
	WindowLocator,
	SpawnParams,
	ShowWindowParams,
} from "./services/window/types";
// WindowClient
export { WindowBounds } from "./services/window/types";
// WorkspaceClient
export { WorkspaceEventName } from "./services/workspace/types";
// FinsembleWindow
export { WindowEventName, WrapState } from "./services/window/types";

/**
 * Following are legacy exports that have been used by customers primarily to build custom
 * services and storage adapters. These will be deprecated over time as we roll out simplified
 * mechanisms for services and adapters.
 */

/**
 * Used for build storage adapters
 */
export const models = {
	baseStorage,
};

/**
 * @ignore Don't display these in Typedoc output
 */
export { FinsembleWindow, baseService, UserNotification, DependencyManager, System, Util };

export const Clients = {
	RouterClient,
	StorageClient,
	LauncherClient,
	DistributedStoreClient,
	LinkerClient,
	WindowClient,
	WorkspaceClient,
	DialogManager,
	AuthenticationClient,
	ConfigClient,
	DragAndDropClient,
	Logger,
	/** @deprecated */
	baseClient: _BaseClient,
	SearchClient,
	HotkeyClient,
	NotificationClient,
};
